import React, {useState} from 'react';

import audioPlaceholder from 'assets/icons/colored/audio-preview-2.svg';
import audioFilledPlaceholder from 'assets/icons/colored/audio-preview-3.svg';
import cn from 'classnames';

import bem from 'client/services/bem';
import {useLanguage} from 'client/services/hooks';
import {getFileNameFromValue} from 'client/services/utils/files';

import AppButton from 'client/common/buttons/app-button';
import FileButton from 'client/common/buttons/file-button';
import InputWrapper, {InputWrapperProps} from 'client/common/inputs/input-wrapper';
import ConfirmationModal from 'client/common/modals/confirmation-modal';
import ConfirmationPopover from 'client/common/popovers/confirmation-popover';

import {Translation} from 'client/models/language/types';

import cssModule from './sound-file-input.module.scss';

const b = bem('sound-file-input', {cssModule});

const MAX_SIZE_BYTES = 3 * 1024 * 1024;

type SoundFileInputProps = Omit<InputWrapperProps, 'readOnly' | 'name'> & {
  name: string;
  value?: File | string | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  onRemove?: () => void;
  placeholder?: Translation;
  replacePlaceholder?: Translation;
  formatErrorMessage?: Translation;
  sizeErrorMessage?: Translation;
  className?: string;
};

const SoundFileInput: React.FC<SoundFileInputProps> = ({
  name,
  value = null,
  onChange,
  disabled = false,
  onRemove,
  placeholder = '',
  replacePlaceholder = '',
  formatErrorMessage = '',
  sizeErrorMessage = '',
  errorMessage = '',
  className = '',
}) => {
  const [validationError, setValidationError] = useState<Translation | null>(null);
  const lang = useLanguage('COMMON');

  const filename = getFileNameFromValue(value);

  const handleChange = (file: File, _filename: string, fileType: string) => {
    if (fileType !== 'audio/mpeg') {
      setValidationError(formatErrorMessage);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setValidationError(sizeErrorMessage);
      return;
    }
    onChange(file);
  };

  const handleRemove = () => {
    onRemove?.();
    onChange(null);
  };

  return (
    <InputWrapper
      name={name}
      className={cn(b('', {error: !!errorMessage}), className)}
      errorMessage={errorMessage}
      classNames={{content: b('input-wrapper-content')}}
    >
      <div className={b('controls')}>
        <FileButton
          className={b('file-button')}
          name={name}
          disabled={disabled}
          onChange={handleChange}
          acceptFormats=".mp3"
          asWrap={true}
          label={
            <div className={b('container', {'without-value': !value})}>
              <img
                src={value ? audioFilledPlaceholder.toString() : audioPlaceholder.toString()}
                className={b('placeholder-icon')}
                alt=""
              />
              <span className={b('filename', {'with-value': !!value, 'without-value': !value})}>
                {value ? (
                  <>
                    <span>{replacePlaceholder}</span>
                    <span className={b('filename-text')}>{filename}</span>
                  </>
                ) : (
                  placeholder
                )}
              </span>
            </div>
          }
        />
        {!disabled && value && (
          <div className={b('buttons-wrap')}>
            <ConfirmationPopover
              title={lang.DELETE_THE_AUDIO}
              okButtonProps={{label: lang.DELETE_CONFIRM, onClick: handleRemove}}
            >
              <AppButton
                className={b('button')}
                title={lang.DELETE}
                iconConfig={{name: 'close', width: 22, height: 22, className: b('delete-icon')}}
                asWrap={true}
              />
            </ConfirmationPopover>
          </div>
        )}
      </div>

      {validationError && (
        <ConfirmationModal
          show={true}
          onConfirm={() => setValidationError(null)}
          onClose={() => setValidationError(null)}
          message={<span className={b('error-message')}>{validationError}</span>}
          clientSide={true}
          title={lang.UPLOAD_ERROR}
          buttonConfirm={{label: lang.OK, color: 'devices'}}
        />
      )}
    </InputWrapper>
  );
};

export default SoundFileInput;
